export { calculate月間介護保険料 } from "./介護保険"
export { calculate年間労災保険料 } from "./労災保険"
export { calculate月間雇用保険料 } from "./雇用保険"
export { calculate月間子育て支援金 } from "./子育て支援金"
export  { calculate月間年金保険料 } from "./年金保険";
export  { calculate月間健康保険料 } from "./健康保険";

export type 健康保険被保険者区分 = "協会けんぽ" | "健康保険組合" | "国民健康保険" | "国民健康保険組合" | "後期高齢者" | "被扶養者" | "被保険者ではない"
export type 年金被保険者区分 = '会社員' | '自営業者' | '配偶者被扶養者';
export type 介護保険被保険者区分 = "介護保険第1号被保険者" | "介護保険第2号被保険者" | "介護保険被保険者ではない";

export const 社会保険定数 = {
  国民年金月額: 16_980,             // 2024年度・第1号月額

  厚生年金本人負担率: 0.0915,        // 18.3% ÷ 2（労使折半）
  健保本人負担率: 0.0500,           // 協会けんぽ全国平均 10% ÷ 2

  国保所得割率: 0.10,               // 医療分＋後期支援金分の合算（全国平均概算）
  国保介護所得割率: 0.015,          // 40〜64歳の介護分（国保内・全国平均概算）

  後期高齢者所得割率: 0.09,         // 全国平均
  後期高齢者基礎控除額: 430_000,    // 賦課基準額 = 前年所得 - 43万
  後期高齢者均等割年額: 50_000,     // 被扶養者が75歳到達時の概算均等割
  子育て支援金: {
    開始年: 2026
  }
} as const;

export type 事業種別 = "一般" | "農林水産" | "建設"

type 年金保険情報 = {
  厚生年金本人負担率: number,
  国民年金月額: number,
}

type 健康保険情報 = {
  健康保険料率: number, // 協会けんぽ等用（例: 0.10）
  国保_均等割額: number, // 市区町村ごとに違う定額料金
  国保_所得割率: number, // 市区町村ごとに違う率
}

type 介護保険情報 = {
  医療保険に加入しているか: boolean,
  判定月の月末時点で日本に住民票があるか: boolean,
  適用除外施設に入所しているか: boolean,
  標準報酬月額: number,
  労使折半する前の介護保険料率: number,
  市区町村ごとの基準額: number,
  所得段階ごとの乗数: number,
}

type 雇用保険情報 = {
  社長や役員等のような雇う側かどうか: boolean,
  is週20時間以上かつ31日以上の雇用見込み: boolean,
  残業代_通勤交通費_各種手当などをすべて足した対象月の実際の総支給額: number,
  事業種別: 事業種別,
}

type 労災保険情報 = {
  特別加入制度に加入している: boolean,
  給付基礎日額: number,
  労災保険料: number,
}

/** 社会保険料計算関数への入力 */
export type 社会保険入力データ = {
  対象年: number, //全員入力必須
  対象月: number, //全員入力必須
  生年月日_年: number, //全員入力必須
  生年月日_月: number, //全員入力必須
  生年月日_日: number, //全員入力必須
  健康保険被保険者区分: 健康保険被保険者区分, //全員入力必須
  年金被保険者区分: 年金被保険者区分,

  標準報酬月額: number;
  前年の合計所得: number;

  年金保険情報: 年金保険情報;

  健康保険情報: 健康保険情報;

  介護保険情報: 介護保険情報;

  雇用保険情報: 雇用保険情報;

  労災保険情報: 労災保険情報;
};

export type 社会保険出力データ = {
  月間健康保険料: number,
  月間年金保険料: number,
  月間介護保険料: number,
  月間雇用保険料: number,
  年間労災保険料: number,
  月間子育て支援金: number,
  月間本人負担合計: number,
  年間本人負担合計: number,
};

export type 第2号年金被保険者用の社会保険料計算入力データ = {
  対象年度: number;
  対象月: number;
  残業代や交通費含む対象月の給与額面: number;
  標準報酬月額: number; // 計算済みとして受け取る
  その月にもらったボーナス額面: number; 
  四十歳以上六十四歳以下か: boolean;
  厚生年金保険料率_個人負担: number; // 例: 0.0915 (9.15%)
  健康保険料率_全体: number;          // 例: 0.0998 (東京都の協会けんぽ等)
  介護保険料率_個人負担: number;     // 例: 0.008 (0.8%)
  雇用保険率_個人負担: number;       // 例: 0.006 (0.6%)
  年金事務所に健康保険適用除外承認申請をして認められたか?: boolean;
}

export type 第1号年金被保険者用の社会保険料計算入力データ = {
  対象年度: number;
  対象月: number;
  前年の総所得金額: number; // 第1号は「今年の給与」ではなく「前年の所得(利益)」で計算します

  四十歳以上六十四歳以下か: boolean;

  // --- 国民年金 ---
  // 国が毎年定める定額（※％ではない！）
  国民年金保険料_月額: number; // 例: 17510 (令和7年度予定など)

  // --- 国民健康保険（国保） ---
  // 自治体によって異なるため、外部から注入（手動設定orマスターデータ参照）
  国保_医療分_所得割率: number;
  国保_医療分_均等割額: number; // 1人あたり〇円
  
  国保_支援金分_所得割率: number;
  国保_支援金分_均等割額: number;
  
  国保_介護分_所得割率: number;  // 40〜64歳のみ使用
  国保_介護分_均等割額: number;  // 40〜64歳のみ使用

  // ※第1号なので「雇用保険率」「労災保険」などのプロパティは存在しません！
}

export type 途中で切り替わった人用の社会保険料計算入力データ = {
  // --------------------------------------------------
  // 1. タイムライン（いつ・どう変わったか）
  // --------------------------------------------------
  前職の種類: "会社員・公務員" | "自営業" | "アルバイト・学生";
  
  退職した月: number; // 例: 10（月）
  退職日は月末か: boolean; // trueならその月まで第2号、falseなら前月まで第2号

  新しく就職したか: boolean;
  // 以下は「新しく就職したか === true」の場合のみ存在するためオプショナル(?)にする
  新しく就職した月?: number; 
  // 入社した月と退職した月が同じ（空白期間ゼロ）かどうかの判定に使う
  同月内での転職か?: boolean; 

  // --------------------------------------------------
  // 2. 金額・パラメータ（計算に必ず使うもの）
  // --------------------------------------------------
  四十歳以上六十四歳以下か: boolean;

  // 【第1号になる期間がある場合（2→1、1→1など）に必須】
  前年の総所得金額?: number;

  // 【第2号になる期間がある場合（1→2、2→2など）に必須】
  新しい職場の見込み月給?: number;
  新しい職場の見込みボーナス?: number;
}

export type アルバイトの人用の社会保険料計算入力データ = {
  対象年度: number;
  
  // 1. そもそも自分でバイト先の保険に入っているか（106万・130万の壁を越えた場合など）
  バイト先の社会保険_健康保険・厚生年金_に加入しているか: boolean;

  // 2. 扶養の判定用
  扶養してくれる人はいるか: boolean;
  その扶養者の種類: "1年間ずっと会社員・公務員" | "ずっと自営業・フリーランス・無職" | "年の途中で退職や独立をする（した）予定";
  扶養者が退職・独立した月?: number; // 前回のロジックを活かすなら必須

  // 3. 第1号（自分で払う）になった場合の免除判定
  学生納付特例を適用しているか: boolean;

  // 4. 国保の計算用
  前年の所得: number;

  isAged40To64: boolean;

  /** バイト先で厚生年金・健康保険に加入している場合の試算用（任意） */
  バイトの月給額面?: number;
  標準報酬月額?: number;
  その月のボーナス額面?: number;
}